import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  PlanCard,
  CTASection,
} from "../../components/marketing-components.js";

// Static plan mapping for public display
const PLAN_FEATURES = {
  free: {
    description: "Try it out",
    features: [
      "1 website",
      "1 scan per month",
      "10 pages per scan",
      "Accessibility score and report",
      "WCAG 2.1 AA scanning",
    ],
    price: 0,
  },
  starter: {
    description: "Perfect for getting started",
    features: [
      "1 website",
      "2 scans per month",
      "150 pages per scan",
      "Score history and trends",
      "WCAG 2.1 AA scanning",
      "Issue grouping and reports",
    ],
    price: 10,
  },
  growth: {
    description: "Growing accessibility practice",
    features: [
      "3 websites",
      "4 scans per month",
      "250 pages per scan",
      "Score history and trends",
      "Document scanning",
      "Accessibility statements",
    ],
    price: 30,
  },
  pro: {
    description: "Enterprise grade",
    features: [
      "5 websites",
      "8 scans per month",
      "1000 pages per scan",
      "Score history and trends",
      "Regression detection",
      "Document scanning",
      "Accessibility statements",
    ],
    price: 99,
  },
} as const;

export function PlansPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Simple, transparent pricing"
        subtitle="All plans include scanning, detailed reporting, and accessibility statement generation."
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>Choose your plan</h2>
          <p className="text-center mb-4">
            Start free with 10 pages. Paid plans include a 14-day free trial.
            Upgrade anytime.
          </p>

          <div className="plans-grid">
            <PlanCard
              name="Free"
              description="Try it out"
              price={0}
              priceUnit=""
              features={PLAN_FEATURES.free.features}
              cta={{ label: "Start free", href: "/sign-in" }}
            />

            <PlanCard
              name="Starter"
              description="Perfect for getting started"
              price={10}
              priceUnit="/month"
              features={PLAN_FEATURES.starter.features}
              cta={{ label: "Start trial", href: "/sign-in" }}
            />

            <PlanCard
              name="Growth"
              description="Growing accessibility practice"
              price={30}
              priceUnit="/month"
              features={PLAN_FEATURES.growth.features}
              cta={{ label: "Start trial", href: "/sign-in" }}
              highlighted
            />

            <PlanCard
              name="Pro"
              description="Enterprise grade"
              price={99}
              priceUnit="/month"
              features={PLAN_FEATURES.pro.features}
              cta={{ label: "Start trial", href: "/sign-in" }}
            />
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Plan comparison</h2>

          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Starter</th>
                <th>Growth</th>
                <th>Pro</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Websites</td>
                <td>1</td>
                <td>1</td>
                <td>3</td>
                <td>5</td>
              </tr>
              <tr>
                <td>Scans per month per website</td>
                <td>1</td>
                <td>2</td>
                <td>4</td>
                <td>8</td>
              </tr>
              <tr>
                <td>Pages per scan</td>
                <td>10</td>
                <td>150</td>
                <td>250</td>
                <td>1000</td>
              </tr>
              <tr>
                <td>Score history and trends</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Document scanning</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Regression detection</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Accessibility statements</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Issue grouping & reports</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Element evidence</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Frequently asked questions</h2>

          <div className="faq-list">
            <div className="faq-item">
              <h4>Can I upgrade or downgrade anytime?</h4>
              <p>
                Yes. Your plan changes take effect the next billing cycle.
                Month-to-month, no long-term commitment.
              </p>
            </div>

            <div className="faq-item">
              <h4>What if I need more websites or scans?</h4>
              <p>
                Upgrade anytime. If you need custom limits, contact our sales
                team for enterprise options.
              </p>
            </div>

            <div className="faq-item">
              <h4>Do you offer discounts for annual billing?</h4>
              <p>
                Yes. Contact us to discuss annual plans and volume discounts for
                enterprises.
              </p>
            </div>

            <div className="faq-item">
              <h4>Is there a free plan?</h4>
              <p>
                Yes. The Free plan lets you scan up to 10 pages once per month,
                forever. No credit card required. Paid plans include a 14-day
                free trial with full features.
              </p>
            </div>

            <div className="faq-item">
              <h4>What happens if I go over my limit?</h4>
              <p>
                We'll notify you. You can upgrade immediately, or pause
                monitoring until your next billing cycle.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can you do custom scanning for my site?</h4>
              <p>
                For sites with complex login, multi-region content, or special
                requirements, contact us about custom scanning plans.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Start scanning for free"
        description="Scan 10 pages free, no credit card required. Upgrade anytime for deeper scanning."
        primaryCTA={{ label: "Get started free", href: "/sign-in" }}
        secondaryCTA={{ label: "Talk to us", href: "/contact" }}
      />
    </PublicLayout>
  );
}
